import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Box, Typography, Button, Dialog, IconButton } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import 'leaflet/dist/leaflet.css';
import { GeoLocation } from '../../types';
import { DESIGN_TOKENS } from '../../styles/theme';
import { useI18n } from '../../i18n';

// Fix for default Leaflet marker icons in React
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface LocationMapProps {
  location?: GeoLocation;
}

// Component to adjust map bounds to the location
function MapCenter({ coords }: { coords: [number, number] }) {
  const map = useMap();
  React.useEffect(() => {
    map.setView(coords, 13);
  }, [map, coords]);
  return null;
}

export const LocationMap: React.FC<LocationMapProps> = ({ location }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { t } = useI18n();

  const coords: [number, number] | null =
    location?.lat && location?.lng ? [Number(location.lat), Number(location.lng)] : null;

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const MapContent = () => (
    <>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <Marker position={coords!}>
        <Popup>
          <Typography variant="body2" fontWeight={600}>{t('maps.location')}</Typography>
          <Typography variant="caption">{location?.city || location?.address}</Typography>
        </Popup>
      </Marker>
      <MapCenter coords={coords!} />
    </>
  );

  if (!coords) {
    return (
      <Box
        sx={{
          height: '100%',
          minHeight: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.02)',
          borderRadius: 2,
          border: '1px dashed rgba(0,0,0,0.1)',
        }}
      >
        <Typography color="text.secondary" variant="body2">
          {t('maps.noGeodata')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          height: '100%',
          width: '100%',
          minHeight: 0,
          borderRadius: DESIGN_TOKENS.borderRadius.card,
          overflow: 'hidden',
          border: `1px solid ${DESIGN_TOKENS.colors.border}`,
          position: 'relative',
          zIndex: 1
        }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<FullscreenIcon />}
          onClick={handleToggleFullscreen}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            color: 'text.primary',
            '&:hover': {
              backgroundColor: '#fff',
            },
          }}
        >
          {t('common.fullScreen')}
        </Button>
        <MapContainer
          center={coords}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
        >
          <MapContent />
        </MapContainer>
      </Box>

      <Dialog
        fullScreen
        open={isFullscreen}
        onClose={handleToggleFullscreen}
      >
        <Box sx={{ position: 'relative', height: '100vh', width: '100vw' }}>
          <IconButton
            onClick={handleToggleFullscreen}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 2000,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': {
                backgroundColor: '#fff',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <MapContainer
            center={coords}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
          >
            <MapContent />
          </MapContainer>
        </Box>
      </Dialog>
    </>
  );
};
